import { longDateFormat } from 'cpfr-common';
import { formatCpDate } from '@cp-shared-19/common-utilities';
import {
    InfoCheckbox,
    preventSubmit,
    Spinner,
    useAnalyticsActionTracker,
    useAnalyticsFormTracker,
    ValidatedInput,
} from '@cp-shared-19/frontend-ui';
import { Button, ErrorMessage, Fieldset, Form, Layout } from '@vwfs-bronson/bronson-react';
import { Formik } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { contactDetailsCheckPagePath } from '../../../../navigation/paths';
import { CharlyDatepicker, legacyToIso, isoToFrench } from 'components/shared/charly-datepicker/CharlyDatepicker';
import { validationSchema } from './validation';
import { isEmpty } from 'lodash';
import { RegistrationErrorNotification } from '../../registration-error';
import { useRegistrationSubmission } from '../useRegistrationSubmission';
import { CustomerInformationWithContract, RegistrationPhysicalFields } from '../RegistrationFormDataTypes';

export const testIds: { [key: string]: string } = {
    contractNumber: 'contract-number',
    dateOfBirth: 'date-of-birth',
    firstName: 'first-name',
    surname: 'surname',
    siren: 'siren',
    confirmPrivacyPolicy: 'confirm-privacy-policy',
    confirmUsageAgreement: 'confirm-usage-agreement',
    submitButton: 'submit-registration-button',
};

type RegistrationPhysicalProps = {
    isRetail?: boolean;
    customer: CustomerInformationWithContract;
};

export const RegistrationPhysical: React.FC<RegistrationPhysicalProps> = ({ customer, isRetail }) => {
    const { t } = useTranslation(['registration', 'loader']);
    const translationPrefix = 'registration-form.person.input-labels';

    const {
        onError: trackOnError,
        onSuccess: trackOnSuccess,
        onTyping,
        onSubmit: trackOnSubmit,
    } = useAnalyticsFormTracker({
        confirmError: 'onConfirmIdentiyAuthFailed',
        confirmSuccess: 'onConfirmConfirmIdentiy',
        startTyping: 'onStartTypingConfirmIdentity',
    });

    const { onAction } = useAnalyticsActionTracker('onFormValidationErrorConfirmIdentity');
    const getErrors = (errors: { [k: string]: string | undefined }) => Object.keys(errors).join(`, `);
    const initialListErrors =
        'contractNumber, firstName, surname, dateOfBirth, confirmTermsAndConditions, confirmPrivacyPolicy';

    const onSubmissionSuccess = useMemo(
        () => (): void => {
            trackOnSuccess();
            window.location.assign(contactDetailsCheckPagePath());
        },
        [trackOnSuccess],
    );
    const onSubmissionError = useMemo(
        () => (): void => {
            trackOnError();
        },
        [trackOnError],
    );

    const { submit, isLoading, errorCode } = useRegistrationSubmission(onSubmissionSuccess, onSubmissionError, false);

    const onSubmit = (values: RegistrationPhysicalFields) => {
        trackOnSubmit();
        const { contractNumber, customerType, dateOfBirth, firstName, surname, siren } = values;
        submit({
            contractNumber,
            customerType,
            firstName,
            surname,
            siren,
            dateOfBirth: formatCpDate(dateOfBirth, longDateFormat).format('YYYY-MM-DD'),
        });
    };

    const initialValues: RegistrationPhysicalFields = {
        contractNumber: customer.contractNumber,
        customerType: customer.customerType,
        dateOfBirth: '',
        firstName: '',
        surname: '',
        siren: '',
        confirmPrivacyPolicy: false,
        confirmTermsAndConditions: false,
    };

    return (
        <>
            {isLoading && (
                <Spinner center>
                    <p>{t('genericText', { ns: 'loader' })}</p>
                </Spinner>
            )}
            <Layout.Item>
                <RegistrationErrorNotification errorCode={errorCode} />
            </Layout.Item>
            <Layout.Item default={'1/1'}>
                <Formik initialValues={initialValues} validationSchema={validationSchema(t)} onSubmit={onSubmit}>
                    {(formik) => (
                        <Form
                            onSubmit={(e) => preventSubmit(e)}
                            onChange={() => {
                                onTyping(formik.errors, formik.touched);
                            }}
                        >
                            <Fieldset>
                                <Fieldset.Row>
                                    <CharlyDatepicker
                                        label={`${t(`${translationPrefix}.birth-date.label`)}`}
                                        required={true}
                                        displayFormat="dd/mm/yyyy"
                                        value={legacyToIso(formik.values.dateOfBirth)}
                                        onChange={(iso) => formik.setFieldValue('dateOfBirth', isoToFrench(iso))}
                                    />
                                    {formik.touched.dateOfBirth && formik.errors.dateOfBirth && (
                                        <ErrorMessage>{formik.errors.dateOfBirth}</ErrorMessage>
                                    )}
                                </Fieldset.Row>
                                <Fieldset.Row>
                                    <ValidatedInput
                                        requiredText=" *"
                                        required={true}
                                        aria-required={true}
                                        className="c-form-field--no-floating"
                                        label={t(`${translationPrefix}.name.label`)}
                                        name="firstName"
                                        testId={testIds.firstName}
                                        inputId="first-name-reqistration-phisical"
                                        tooltip={t('registration-form.head.info-fill-data')}
                                        tooltipTitle={t(`${translationPrefix}.name.tooltip-title`)}
                                        placeholder={t(`${translationPrefix}.name.placeholder`)}
                                        autocomplete="given-name"
                                    />
                                </Fieldset.Row>
                                <Fieldset.Row>
                                    <ValidatedInput
                                        requiredText=" *"
                                        required={true}
                                        aria-required={true}
                                        className="c-form-field--no-floating"
                                        label={t(`${translationPrefix}.surname.label`)}
                                        name="surname"
                                        testId={testIds.surname}
                                        inputId="surname-reqistration-phisical"
                                        tooltip={t('registration-form.head.info-fill-data')}
                                        tooltipTitle={t(`${translationPrefix}.surname.tooltip-title`)}
                                        placeholder={t(`${translationPrefix}.surname.placeholder`)}
                                        autocomplete="family-name"
                                    />
                                </Fieldset.Row>
                                {!isRetail && (
                                    <Fieldset.Row>
                                        <label className="c-form-field__label">
                                            {t(`${translationPrefix}.siren-warning`)}
                                        </label>
                                        <ValidatedInput
                                            requiredText=" *"
                                            required={true}
                                            aria-required={true}
                                            className="c-form-field--no-floating"
                                            label=""
                                            name="siren"
                                            testId={testIds.siren}
                                            inputId="siren-reqistration-phisical"
                                            inputMode={'numeric'}
                                            placeholder={t(`${translationPrefix}.siren.placeholder`)}
                                        />
                                    </Fieldset.Row>
                                )}
                                <InfoCheckbox
                                    required={true}
                                    aria-required={true}
                                    preLinkLabel={t(`${translationPrefix}.agreements.privacy-policy.label`)}
                                    linkLabel={t(`${translationPrefix}.agreements.privacy-policy.link`)}
                                    infoLinkHref={'https://www.vwfs.fr/espace-client/politique-donnees.html'}
                                    fieldName={'confirmPrivacyPolicy'}
                                    testId={testIds.confirmPrivacyPolicy}
                                ></InfoCheckbox>
                                <InfoCheckbox
                                    required={true}
                                    aria-required={true}
                                    preLinkLabel={t(`${translationPrefix}.agreements.terms-and-conditions.label`)}
                                    linkLabel={t(`${translationPrefix}.agreements.terms-and-conditions.link`)}
                                    infoLinkHref={'https://www.vwfs.fr/espace-client/CGU-espace-client.html'}
                                    fieldName={'confirmTermsAndConditions'}
                                    testId={testIds.confirmUsageAgreement}
                                ></InfoCheckbox>

                                <Fieldset.Row>
                                    <Button
                                        full={true}
                                        onClick={() => {
                                            formik.submitForm();
                                            if (!isEmpty(formik.errors) || isEmpty(formik.touched)) {
                                                const errorToString = getErrors(formik.errors).toString();
                                                if (!errorToString) {
                                                    onAction(initialListErrors);
                                                } else onAction(getErrors(formik.errors));
                                            }
                                        }}
                                        testId={testIds.submitButton}
                                    >
                                        {t(`registration-form.button-labels.confirm`)}
                                    </Button>
                                </Fieldset.Row>
                            </Fieldset>
                        </Form>
                    )}
                </Formik>
            </Layout.Item>
        </>
    );
};
